import { ChannelType } from "src/types/cep";
import { CEP_CONTROL_PLANE_COLUMNS } from "src/cep/types";
import { isPresent } from "src/types/utils";

/**
 * Parses the channel type from a cep_managed_model.type or MessageEventFilter value,
 * which all follow the convention of starting with the channel name.
 */
export const getManagedModelChannel = (
  type: string | null | undefined,
): ChannelType | undefined => {
  if (!type) return undefined;
  if (type.startsWith("email")) return ChannelType.Email;
  if (type.startsWith("sms")) return ChannelType.Sms;
  if (type.startsWith("push")) return ChannelType.Push;
  return undefined;
};

/**
 * Managed models are not always message events — `subscription_topics` has no event,
 * so it surfaces as a plain relation. Identify one from the relationship pointing at it
 * rather than from the filter option type.
 */
export const isCepManagedModel = ({
  modelId,
  relationships,
}: {
  modelId: string | null | undefined;
  relationships: readonly {
    to_model: { id: unknown; cep_managed_model?: unknown };
  }[];
}): boolean =>
  isPresent(modelId) &&
  relationships.some(
    ({ to_model }) =>
      String(to_model.id) === String(modelId) &&
      Boolean(to_model.cep_managed_model),
  );

/** These columns hold opaque IDs, so a selector that resolves them to names takes priority over the generic value input. */
export const isCepControlPlaneProperty = ({
  isCepManagedModel,
  columnName,
}: {
  isCepManagedModel: boolean | undefined;
  columnName: string | null | undefined;
}): boolean =>
  Boolean(isCepManagedModel) &&
  Boolean(
    columnName && CEP_CONTROL_PLANE_COLUMNS.has(columnName.toLowerCase()),
  );

/** Sentinel value that tells the stats resolver to query all time. */
export const ALL_TIME = "-1";

/**
 * Offsets reflect whatever DST state the client is in right now
 * (e.g. EST shows UTC-5, EDT shows UTC-4).
 */
export function formatUtcOffset(timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date());
  const raw = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  return raw.replace(/^GMT/, "UTC") || "UTC";
}

/** The IANA time zone the user's browser is currently in. */
export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * "America/New_York (UTC-4)". Falls back to the bare zone name for the
 * few legacy IANA aliases (e.g. America/Indiana/Valparaiso) that Intl
 * doesn't recognize and would otherwise throw on.
 */
export function formatTimeZoneLabel(timezone: string): string {
  try {
    return `${timezone} (${formatUtcOffset(timezone)})`;
  } catch {
    return timezone;
  }
}

/**
 * Returns an ISO date string for `days` ago, rounded to start of day for better caching.
 */
export function getSinceDate(days: number): string {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
}

export function padHourlySparkline(
  hourly: { hour: string; totalSent: number }[],
): { value: number }[] {
  const buckets = new Map<string, number>();
  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const h = new Date(now);
    h.setMinutes(0, 0, 0);
    h.setHours(h.getHours() - i);
    buckets.set(h.toISOString(), 0);
  }
  for (const h of hourly) {
    const key = new Date(h.hour).toISOString();
    if (buckets.has(key)) {
      buckets.set(key, h.totalSent);
    }
  }
  return Array.from(buckets.values(), (value) => ({ value }));
}
